import { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router';
import {
  LogOut,
  ArrowLeftRight,
  ChevronDown,
  Sun,
  Moon,
  Monitor,
  Loader2,
  Copy,
  Check,
  Gift,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client/react';
import { useAuthStore } from '@/stores/auth-store';
import { isBillingExperimentOn } from '@/features/billing/experiment/is-billing-experiment-on';
import { useTeamStore } from '@/stores/team-store';
import { useThemeStore } from '@/stores/theme-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { setScioVersionCookie, isBehindCfWorker } from '@/lib/sso-cookies';
import { useReferralAccess } from '@/features/referral/hooks/useReferralAccess';
import { analyticsReferralMenuClicked } from '@/lib/analytics-service';
import { SCIO_SWITCH_LOGS, ME_QUERY, type MeQueryResult } from '@scio3/graphql-client';
import { fullLogout } from '@/lib/logout';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { showLoadingOverlay, dismissLoadingOverlay } from '@/components/shared';
import { TeamSwitcher } from './TeamSwitcher';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@scio3/ui';

function getInitials(firstName?: string, lastName?: string, fallbackName?: string): string {
  const first = firstName?.trim()[0];
  const last = lastName?.trim()[0];
  if (first || last) return ((first ?? '') + (last ?? '')).toUpperCase();
  if (fallbackName) {
    const parts = fallbackName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return '?';
}

const THEME_OPTIONS = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
] as const;

interface UserMenuProps {
  compact?: boolean;
}

export function UserMenu({ compact }: UserMenuProps) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const activeTeam = useTeamStore((s) => s.getActiveTeam());
  const navigate = useNavigate();
  const client = useApolloClient();
  const { isRootAdmin, isRootSuperAdmin, isTeamAdmin } = usePermissions();
  const features = useCompanyFeatures();
  // QC #1272 — child / linked sub-accounts can't use Sub-Account Billing
  // (backend rejects activation). Hide the dropdown entry to avoid dead-ends.
  const isChildAccount = !!company?.parentAccountId || !!company?.linkedAccountId;

  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  // Persistent entry point into the referral program. The floating launcher
  // disappears after 3 dismissals, so this menu item keeps "Refer & Earn"
  // findable — it navigates to the full `/app/s/referral` page (in the settings
  // area, with the account sidebar). Gated on paid subscription via useReferralAccess.
  const { entryEnabled: referralEnabled } = useReferralAccess();

  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  // Lapsed account in the billing experiment arm. Those pages are hard-gated in
  // SettingsLayout, so leaving their menu entries here would just bounce the user back to
  // the paywall with a permission toast. The first four entries below (Personal Profile,
  // Company Settings, Account Members, Subscription Plan + Payment Settings) are exactly
  // the allowed set, so only the extras and the More submenu need gating. Logout at the
  // bottom deliberately stays — it is the only way out for a paywalled user.
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);
  const isLapsedExperiment = isAccountInactive && isBillingExperimentOn();
  // OptiSigns-branded surfaces (Integrations, Share Links, DataSources) show only when OptiSigns
  // branding is present — hidden once removed. Mirrors the SettingsLayout sidebar gate so the two
  // render paths stay in sync (scio1 parity).
  const removeOptiSignsBranding = !!me?.brandingData?.removeOptiSignsBranding;
  // White-label page-hide flags (scio1 account.html parity).
  const hideSubscriptionPages = !!me?.brandingData?.hideSubscriptionPages;
  const hidePaymentHistoryPages = !!me?.brandingData?.hidePaymentHistoryPages;
  const hideProofofPlay = !!me?.brandingData?.hideProofofPlay;
  const hideAccountMembersPage = !!me?.brandingData?.hideAccountMembersPage;
  const hideAuditLogging = !!me?.brandingData?.hideAuditLogging;
  const hideMonitorAndAlert = !!me?.brandingData?.hideMonitorAndAlert;
  const hideExternalCommunications = !!me?.brandingData?.hideExternalCommunications;

  const isDefaultTeam = useTeamStore((s) => s.isDefaultTeam);
  const [switchingToClassic, setSwitchingToClassic] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  // Controlled so the referral promo card (a custom button, not a DropdownMenuItem)
  // can close the menu before navigating — matching how the item rows behave.
  const [menuOpen, setMenuOpen] = useState(false);
  const nameRef = useRef<HTMLParagraphElement>(null);
  const [nameTooltipOpen, setNameTooltipOpen] = useState(false);
  const handleNameHover = (open: boolean) => {
    if (!open) {
      setNameTooltipOpen(false);
      return;
    }
    const el = nameRef.current;
    setNameTooltipOpen(!!el && el.scrollWidth > el.clientWidth);
  };

  async function handleSwitchToClassic() {
    if (switchingToClassic) return;
    setSwitchingToClassic(true);
    showLoadingOverlay();

    const teamId = useTeamStore.getState().activeTeamId;
    // 1. Mutation sets user.scioVersion=1 in MongoDB — abort if it fails,
    //    otherwise scio1 reads stale scioVersion=3 → cookie loop.
    try {
      await client.mutate({ mutation: SCIO_SWITCH_LOGS, variables: { teamId } });
    } catch {
      console.error('Failed to switch to classic — mutation failed');
      setSwitchingToClassic(false);
      return;
    }

    // 2. Verify the write is readable — re-query ME until scioVersion≠3.
    //    This prevents scio1's initSCIO20() from reading stale scioVersion=3
    //    (MongoDB replication lag) and overwriting cookie back to 3 → infinite loop.
    for (let i = 0; i < 5; i++) {
      const { data } = await client
        .query<MeQueryResult>({
          query: ME_QUERY,
          fetchPolicy: 'network-only',
        })
        .catch(() => ({ data: null }));
      if (!data?.me?.scioVersion || data.me.scioVersion !== 3) break;
      await new Promise((r) => setTimeout(r, 300));
    }

    setScioVersionCookie('1');
    // Redirect to scio1 with current path + search params preserved.
    const url = new URL(window.location.href);
    if (!isBehindCfWorker()) {
      const scio1 = new URL(import.meta.env.VITE_SCIO1_URL);
      url.href = new URL(url.pathname + url.search, scio1.origin).href;
    }
    // Skip activeTeamId for designManagement — scio1's designManagement.sendAuth()
    // triggers a team-switch reload when $stateParams.teamId differs from
    // the current Meteor team, which causes window.location.reload() that
    // loses all query params (e.g. asset_id). The team is already synced
    // via the scioSwitchLogs mutation above.
    // Use activeTeamId (not teamId) to avoid collision with teamId which is
    // folder-level context for cross-team shared folders (#459).
    const isDesigner = window.location.pathname.endsWith('/designManagement');
    if (!isDesigner) {
      url.searchParams.set('activeTeamId', teamId || '1');
    }
    // scio1's engage page needs tilde-encoded slashes (/ → ~2F) in the path param.
    // Angular UI Router uses ~2F encoding; standard %2F doesn't work for engage (#348).
    const pathParam = url.searchParams.get('path');
    if (pathParam && url.pathname.endsWith('/engageManagement')) {
      url.searchParams.set('path', pathParam.replace(/\//g, '~2F'));
    }
    window.location.href = url.toString();

    // If the user cancels the browser's beforeunload dialog, the page stays alive.
    // After 500ms, if we're still here, dismiss the loading overlay.
    setTimeout(() => {
      setSwitchingToClassic(false);
      dismissLoadingOverlay();
    }, 500);
  }

  async function handleLogout() {
    await fullLogout(client);
    navigate('/signIn');
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? 'icon-sm' : 'sm'}
          className={cn(
            'rounded-full text-muted-foreground hover:text-foreground hover:bg-sidebar-active',
            compact ? 'h-10 w-10' : 'h-auto gap-1.5 py-1 pl-1 pr-3',
          )}
          aria-label={t('User menu')}
          data-testid="user-menu"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {getInitials(me?.firstName, me?.lastName, me?.name)}
          </span>
          {!compact && (
            <>
              <span className="min-w-0 max-w-[120px] truncate text-sm font-medium">
                {activeTeam?.name === 'Default'
                  ? t('Default')
                  : activeTeam?.name?.trim() || t('Account')}
              </span>
              <ChevronDown className="h-3 w-3" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {/* User info header */}
        <div className="px-3 py-2">
          <TooltipProvider>
            <Tooltip open={nameTooltipOpen} onOpenChange={handleNameHover}>
              <TooltipTrigger asChild>
                <p ref={nameRef} className="truncate text-sm font-semibold text-foreground">
                  {me?.name}
                </p>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-xs break-all">
                {me?.name}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {me?.email && (
            <div
              className="group/copy relative cursor-pointer select-none"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(me.email ?? '');
                setEmailCopied(true);
                setTimeout(() => setEmailCopied(false), 1500);
              }}
            >
              <p className="text-xs text-muted-foreground truncate group-hover/copy:text-foreground transition-colors">
                {me.email}
              </p>
              <span className="absolute right-0 top-0 bottom-0 items-center hidden group-hover/copy:flex bg-gradient-to-l from-popover from-50% to-transparent pl-5">
                {emailCopied ? (
                  <Check className="h-3 w-3 text-success" />
                ) : (
                  <Copy className="h-3 w-3 text-muted-foreground" />
                )}
              </span>
            </div>
          )}
          {/* Inline theme toggle */}
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <ViewToggle
              value={theme}
              onValueChange={(v) => setTheme(v as 'light' | 'dark' | 'system')}
              options={THEME_OPTIONS.map(({ value, icon, label }) => ({
                value,
                icon,
                label: t(label),
              }))}
            />
          </div>
        </div>
        {/* Refer & Earn promo card (replaces the plain menu item below) — a
            tinted, single-tap card sitting directly under the mode selector.
            The whole card is the click target → opens the full Refer & Earn
            page, the same nav the old item used. Gated on the same referral
            flag. The gift tile reuses the shared `.ref-gift-chip` gradient so
            it matches the sidebar chip + widget hero. */}
        {referralEnabled && !isLapsedExperiment && (
          <div className="px-2 pb-1 pt-0.5">
            <button
              type="button"
              onClick={() => {
                analyticsReferralMenuClicked();
                setMenuOpen(false);
                navigate('/app/s/referral');
              }}
              data-testid="user-menu-referral"
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-primary/20 bg-secondary px-3 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-primary/15"
            >
              <span className="ref-gift-chip flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-white shadow-[0_8px_16px_-8px_rgba(0,208,110,0.6)]">
                <Gift className="h-[18px] w-[18px]" strokeWidth={2} />
              </span>
              <span className="whitespace-nowrap text-sm font-semibold text-foreground">
                {t('Refer & Earn')}
              </span>
              <span className="ml-auto shrink-0 whitespace-nowrap rounded-full border border-primary/25 bg-card px-2.5 py-0.5 text-[11px] font-bold text-secondary-foreground">
                Get $25
              </span>
            </button>
          </div>
        )}
        <TeamSwitcher />
        {/* Main items */}
        <DropdownMenuItem onClick={() => navigate('/app/s/personal-profile')}>
          {t('Personal Profile')}
        </DropdownMenuItem>
        {isRootAdmin && !isWhiteLabel && (
          <DropdownMenuItem onClick={() => navigate('/app/s/company-profile')}>
            {t('Company Settings')}
          </DropdownMenuItem>
        )}
        {!hideAccountMembersPage && (
          <DropdownMenuItem onClick={() => navigate('/app/s/account-members')}>
            {t('Account Members')}
          </DropdownMenuItem>
        )}
        {isRootAdmin && !hideSubscriptionPages && (
          <>
            <DropdownMenuItem onClick={() => navigate('/app/s/subscription-plan')}>
              {t('Subscription Plan')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/app/s/payment')}>
              {t('Payment Settings')}
            </DropdownMenuItem>
          </>
        )}
        {isRootAdmin && isDefaultTeam && !isWhiteLabel && !isLapsedExperiment && (
          <DropdownMenuItem onClick={() => navigate('/app/s/order-device')}>
            {t('Order Devices')}
          </DropdownMenuItem>
        )}
        {isRootSuperAdmin && !isWhiteLabel && !isLapsedExperiment && (
          <DropdownMenuItem onClick={() => navigate('/app/s/preference-settings')}>
            {t('Preferences')}
          </DropdownMenuItem>
        )}

        {/* More submenu - show for team admins and above */}
        {(isRootAdmin || isTeamAdmin) && !isLapsedExperiment && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>{t('More')}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56">
                {/* ROOT admin items (matches scio1 isRootAdmin) */}
                {isRootAdmin && (
                  <>
                    {/* OptiSigns-only — hidden for white-label (scio1 parity) */}
                    {!isWhiteLabel && (
                      <DropdownMenuItem onClick={() => navigate('/app/s/operational-schedule')}>
                        {t('Operational Schedule')}
                      </DropdownMenuItem>
                    )}
                    {!hidePaymentHistoryPages && (
                      <DropdownMenuItem onClick={() => navigate('/app/s/payment-history')}>
                        {t('Billing History')}
                      </DropdownMenuItem>
                    )}
                    {!isChildAccount && !isWhiteLabel && (
                      <DropdownMenuItem onClick={() => navigate('/app/s/sub-account-billing')}>
                        {t('Sub-Account Billing')}
                      </DropdownMenuItem>
                    )}
                    {/* <DropdownMenuItem onClick={() => navigate('/app/s/coupons')}>
                      {t('Coupons')}
                    </DropdownMenuItem> */}
                    {!isWhiteLabel && (
                      <DropdownMenuItem onClick={() => navigate('/app/s/ordered-device')}>
                        {t('Order History')}
                      </DropdownMenuItem>
                    )}
                    {!hideProofofPlay && (
                      <DropdownMenuItem onClick={() => navigate('/app/s/proof-of-play-settings')}>
                        {t('Proof of Play')}
                      </DropdownMenuItem>
                    )}
                  </>
                )}

                {/* Branding settings — OptiSigns-only, hidden for white-label (scio1 parity) */}
                {isRootAdmin && !isWhiteLabel && (
                  <DropdownMenuItem onClick={() => navigate('/app/s/branding-settings')}>
                    {t('Branding')}
                  </DropdownMenuItem>
                )}

                {/* Advanced admin items - ROOT admin only. Audit/Monitoring/External-Com are
                    shown on white-label ONLY when the matching add-on feature is enabled (scio1 parity). */}
                {isRootAdmin && (
                  <>
                    {!hideAuditLogging &&
                      (!isWhiteLabel || company?.feature?.securityAuditLogging) && (
                        <DropdownMenuItem onClick={() => navigate('/app/security-audit-logging')}>
                          {t('Audit Log')}
                        </DropdownMenuItem>
                      )}
                    {!hideMonitorAndAlert && (!isWhiteLabel || company?.feature?.alertPolicy) && (
                      <DropdownMenuItem onClick={() => navigate('/app/s/monitoring-alert-rules')}>
                        {t('Monitoring Alert')}
                      </DropdownMenuItem>
                    )}
                    {/* SAML & API Keys hidden for white-label (matches scio1) */}
                    {!isWhiteLabel && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/app/s/saml-settings')}>
                          {t('SAML Single Sign On')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/app/s/apikeys')}>
                          {t('API Keys')}
                        </DropdownMenuItem>
                      </>
                    )}
                    {!hideExternalCommunications &&
                      (!isWhiteLabel || company?.feature?.externalCom) && (
                        <DropdownMenuItem onClick={() => navigate('/app/s/external-coms')}>
                          {t('External Communications')}
                        </DropdownMenuItem>
                      )}
                  </>
                )}

                {/* Team-level items visible to team admins+ (matches scio1 isTeamAdmin).
                    OptiSigns-branded — hidden once OptiSigns branding is removed (SettingsLayout parity). */}
                {!removeOptiSignsBranding && (
                  <DropdownMenuItem onClick={() => navigate('/app/s/integrations')}>
                    {t('Integrations')}
                  </DropdownMenuItem>
                )}
                {company?.feature?.shareLink && !removeOptiSignsBranding && (
                  <DropdownMenuItem onClick={() => navigate('/app/s/share-links-admin')}>
                    {t('Share Links')}
                  </DropdownMenuItem>
                )}
                {!removeOptiSignsBranding && (
                  <DropdownMenuItem onClick={() => navigate('/app/s/datasources-admin')}>
                    {t('DataSources')}
                  </DropdownMenuItem>
                )}
                {features.smallappManifest && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/app/dev/smallapp-playground')}
                  >
                    {t('SmallApp Playground')}
                  </DropdownMenuItem>
                )}
                {isRootAdmin && features.contentApproval && (
                  <DropdownMenuItem onClick={() => navigate('/app/s/revue')}>
                    {t('Revue Workflow')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}

        {/* Language */}
        <DropdownMenuSeparator />
        <LanguageSwitcher />

        {/* Switch to Classic & Logout */}
        <DropdownMenuSeparator />
        {/* scio1 parity: the UI-version switch (scio1's "Try New Interface") is hidden on
            white-label sites (topNavigation.html `!isWhiteLabel`) — hide "Switch to Classic" too. */}
        {!isWhiteLabel &&
          (isBehindCfWorker()
            ? !(company?.scioVersion != null && company.scioVersion > 1)
            : !!import.meta.env.VITE_SCIO1_URL) && (
            <DropdownMenuItem onClick={handleSwitchToClassic} disabled={switchingToClassic}>
              {switchingToClassic ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowLeftRight className="mr-2 h-4 w-4" />
              )}
              {switchingToClassic ? t('Switching…') : t('Switch to Classic')}
            </DropdownMenuItem>
          )}
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          {t('Log Out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
